/**
 * `useOfficeItem`: the read of the open Outlook message, modelled as a query.
 *
 * The Office probe is a one-shot, side-effectful read of the host, so it lives in
 * a TanStack Query (`queryFn`) rather than a `useEffect`: the same loading /
 * settled state machine the rest of the app uses, and it runs exactly once. The
 * probe never throws for "not in Outlook" (that is the valid `standalone` data
 * state), so the query only errors on a genuine unexpected failure, and even then
 * the page falls back to the standalone demo.
 *
 * Everything here is READ-ONLY: it reads the open item (via `office-js.ts`) and
 * derives display helpers. It writes nothing anywhere.
 */

import { useQuery } from "@tanstack/react-query";

import { logger } from "@/lib/logger";

import { type OfficeMail, type OfficeProbe, probeOfficeItem } from "./office-js";

/** Read the open Outlook item (or detect that we are not in Outlook). Runs once. */
export function useOfficeItem() {
  return useQuery<OfficeProbe>({
    queryKey: ["office-item"],
    queryFn: async () => {
      const probe = await probeOfficeItem();
      logger.debug("[addin] office probe", { kind: probe.kind });
      return probe;
    },
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/** Drop a leading greeting / trailing signature / quoted-reply line from the enquiry body. */
function isNoiseLine(line: string): boolean {
  if (line.startsWith(">")) {
    return true; // quoted reply
  }
  return /^(hi|hello|dear|regards|thanks|thank you|kind regards|best regards|cheers|sent from)\b/i.test(
    line,
  );
}

/**
 * Best-effort client-side split of the mail body into candidate enquiry lines.
 *
 * This is only for what the pane DISPLAYS ("lines we read"); the authoritative
 * line breakdown comes back from the read-only `/api/quote/preview` engine call.
 * We keep it conservative: non-empty, de-noised lines, capped so a long signature
 * or quoted history cannot flood the narrow pane.
 */
export function extractEnquiryLines(body: string, limit = 12): string[] {
  return body
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0 && !isNoiseLine(line))
    .slice(0, limit);
}

/** Strip CR/LF from a value going into a MIME header (avoids header injection). */
function sanitizeHeader(value: string): string {
  return value.replace(/[\r\n]+/g, " ").trim();
}

/**
 * Assemble a minimal RFC 5322 message from the read item so the existing
 * `/api/quote/preview` endpoint (which parses raw MIME) can run its full
 * glass-box pipeline. Purely local string assembly, nothing is sent.
 */
export function buildMimeFromMail(mail: OfficeMail): string {
  const fromHeader = mail.senderEmail
    ? mail.senderName
      ? `${sanitizeHeader(mail.senderName)} <${sanitizeHeader(mail.senderEmail)}>`
      : sanitizeHeader(mail.senderEmail)
    : "unknown@unknown.invalid";

  const headers = [
    `From: ${fromHeader}`,
    `Subject: ${sanitizeHeader(mail.subject)}`,
    "MIME-Version: 1.0",
    'Content-Type: text/plain; charset="utf-8"',
  ];

  return `${headers.join("\r\n")}\r\n\r\n${mail.body}`;
}
